import {
  DNSServer,
  NTPServer,
  PatchMetadata,
  PatchOva,
  Request,
  RequestId,
  RequestName,
  RequestType,
  SMTPConfiguration,
  SNMPConfiguration
} from 'vrlcm-gui-types';
import { Http } from 'vrlcm-gui-utils';
import { RequestApi, RequestApiUri } from '../request';
import { LcopsApiUri } from './uri';

export class ServersAndAccountsApi {
  public static refreshOnlinePatches = (): Promise<RequestId> => {
    return Http.post(LcopsApiUri.Settings.GET_AVAILABLE_PATCHES_URL, {}).then(
      res => res.data
    );
  };
  public static refreshOnlinePSPack = (): Promise<RequestId> => {
    return Http.post(LcopsApiUri.Settings.GET_AVAILABLE_POLICIES_URL, {}).then(
      res => res.data
    );
  };
  public static downloadPatch = (patch: PatchMetadata): Promise<RequestId> => {
    const body = {
      productId: patch.product.productId,
      productVersion: patch.product.productVersion,
      patchId: patch.patchId
    };
    return Http.post(LcopsApiUri.Settings.DOWNLOAD_PATCH_URL, body).then(
      res => res.data
    );
  };
  public static deletePatch = (patch: PatchMetadata): Promise<RequestId> => {
    const body = {
      productId: patch.product.productId,
      productVersion: patch.product.productVersion,
      patchId: patch.patchId
    };
    return Http.post(LcopsApiUri.Settings.DELETE_PATCH_URL, body).then(
      res => res.data
    );
  };
  public static addOrUpdateNTPServer = (
    ntpServer: NTPServer,
    editViewFlag?: boolean
  ): Promise<Response> => {
    if (editViewFlag) {
      return Http.put(
        LcopsApiUri.Settings.UPDATE_NTP_SERVER_URL,
        ntpServer
      ).then(res => res.data);
    } else {
      return Http.post(LcopsApiUri.Settings.NTP_SERVER_URL, ntpServer).then(
        res => res.data
      );
    }
  };
  public static getNTPServers = (): Promise<NTPServer[]> => {
    return Http.get(LcopsApiUri.Settings.NTP_SERVER_URL).then(res => res.data);
  };

  public static deleteNTPServers = (ntpServer: NTPServer): Promise<null> => {
    return Http.delete(LcopsApiUri.Settings.DELETE_NTP_SERVER_URL, {
      data: ntpServer
    }).then(res => res.data);
  };

  public static async getSnmpConfigurations() {
    const response = await Http.get<SNMPConfiguration[]>(
      LcopsApiUri.SettingsV2.PRODUCT_SNMP_URL
    );
    return response.data;
  }

  public static async deleteSnmpConfiguration(snmpName: string) {
    await Http.delete(
      LcopsApiUri.SettingsV2.PRODUCT_SNMP_BY_NAME_URL(snmpName)
    );
  }

  public static async addSnmpConfiguration(
    snmpConfiguration: SNMPConfiguration
  ) {
    const response = await Http.post<SNMPConfiguration>(
      LcopsApiUri.SettingsV2.PRODUCT_SNMP_URL,
      snmpConfiguration
    );
    return response.data;
  }

  public static async editSnmpConfiguration(
    snmpConfiguration: SNMPConfiguration
  ) {
    const response = await Http.put<SNMPConfiguration>(
      LcopsApiUri.SettingsV2.PRODUCT_SNMP_URL,
      snmpConfiguration
    );
    return response.data;
  }

  public static async updateSnmpConfiguration(
    snmpConfiguration: SNMPConfiguration,
    editMode: boolean
  ) {
    if (editMode) {
      return ServersAndAccountsApi.editSnmpConfiguration(snmpConfiguration);
    } else {
      return ServersAndAccountsApi.addSnmpConfiguration(snmpConfiguration);
    }
  }

  public static async getSmtpConfigurations() {
    const response = await Http.get<SMTPConfiguration>(
      LcopsApiUri.SettingsV2.NOTIFICATION_SMTP_URL
    );
    return response.data;
  }

  public static async deleteSmtpConfiguration() {
    await Http.delete(LcopsApiUri.SettingsV2.NOTIFICATION_SMTP_URL);
  }

  public static async addSmtpConfiguration(
    smtpConfiguration: SMTPConfiguration
  ) {
    const response = await Http.post<SMTPConfiguration>(
      LcopsApiUri.SettingsV2.NOTIFICATION_SMTP_URL,
      smtpConfiguration
    );
    return response.data;
  }

  public static async editSmtpConfiguration(
    smtpConfiguration: SMTPConfiguration
  ) {
    const response = await Http.put<SMTPConfiguration>(
      LcopsApiUri.SettingsV2.NOTIFICATION_SMTP_URL,
      smtpConfiguration
    );
    return response.data;
  }

  public static async updateSmtpConfiguration(
    smtpConfiguration: SMTPConfiguration,
    editMode: boolean
  ) {
    if (editMode) {
      return ServersAndAccountsApi.editSmtpConfiguration(smtpConfiguration);
    } else {
      return ServersAndAccountsApi.addSmtpConfiguration(smtpConfiguration);
    }
  }

  public static async sendSmtpTestEmail(email: string) {
    const response = await Http.get(
      LcopsApiUri.NOTIFICATION_SMTP_TEST_URL(email)
    );
    return response.data;
  }

  public static deleteDNSServers = (dnsServer: DNSServer): Promise<null> => {
    return Http.delete(LcopsApiUri.Settings.DELETE_DNS_SERVER_URL, {
      data: dnsServer
    }).then(res => res.data);
  };
  public static getDNSServers = (): Promise<DNSServer[]> => {
    return Http.get(LcopsApiUri.Settings.DNS_SERVER_URL).then(res => res.data);
  };
  public static deletePatchOva = (patch: PatchOva): Promise<RequestId> => {
    return Http.post(LcopsApiUri.Settings.DELETE_PATCH_OVA_URL, patch).then(
      res => res.data
    );
  };
  public static addOrUpdateDNSServer = (
    dnsServer: DNSServer,
    editViewFlag?: boolean
  ): Promise<DNSServer> => {
    if (editViewFlag) {
      return Http.put(
        LcopsApiUri.Settings.UPDATE_DNS_SERVER_URL,
        dnsServer
      ).then(res => res.data);
    } else {
      return Http.post(LcopsApiUri.Settings.DNS_SERVER_URL, dnsServer).then(
        res => res.data
      );
    }
  };

  public static getLatestBinarySyncStatus = async (): Promise<
    Request | undefined
  > => {
    const requests = await RequestApi.getRequestWithQueryOptions({
      filter: {
        requestTypes: [RequestName.VcfSourceMapping]
      },
      sortByDirection: 'DESC',
      sortBy: 'lastUpdatedOn',
      limit: 1
    });
    return requests.length > 0 ? requests[0] : undefined;
  };

  public static triggerVcfFipsStatus = async () => {
    const response = await Http.post<Request>(RequestApiUri.REQUESTS_URL, {
      requestReason: 'Get VCF FIPS status',
      requestName: RequestName.vcfFipsStatusCollection,
      requestType: RequestType.VCF_FIPS_STATUS_COLLECTION
    });
    return response.data.vmid;
  };
  public static getLatestVcfFipsStatus = async (): Promise<
    Request | undefined
  > => {
    const requests = await RequestApi.getRequestWithQueryOptions({
      filter: {
        requestTypes: [RequestName.vcfFipsStatusCollection]
      },
      sortByDirection: 'DESC',
      sortBy: 'lastUpdatedOn',
      limit: 1
    });
    return requests.length > 0 ? requests[0] : undefined;
  };

  public static triggerVcfCeipStatus = async () => {
    const response = await Http.post<Request>(RequestApiUri.REQUESTS_URL, {
      requestReason: 'GET SDDC Manager CEIP Status',
      requestName: RequestName.vcfCeipStatusCollection,
      requestType: RequestType.VCF_CEIP_STATUS_COLLECTION
    });
    return response.data.vmid;
  };

  public static vcfVrslcmCompatibilityCheck = async () => {
    const response = await Http.post<Request>(RequestApiUri.REQUESTS_URL, {
      requestReason: 'SDDC Manager - vRSLCM compatibility check',
      requestName: RequestName.vcfVrslcmCompatibilityCheck,
      requestType: RequestType.VCF_VRSLCM_COMPATIBILITY_CHECK
    });
    return response.data.vmid;
  };
}
